if (!customElements.get('v2-product-quick-view')) {
  customElements.define('v2-product-quick-view', class V2ProductQuickView extends HTMLElement {
    connectedCallback() {
      if (this.dialogObserver) return;
      this.dialog = document.getElementById(this.dataset.dialogId);
      if (!this.dialog) {
        this._dialogRetry = setTimeout(() => {
          this._dialogRetry = null;
          this.connectedCallback();
        }, 0);
        return;
      }
      this.dialogBody = this.dialog.querySelector('[data-v2-dialog-body]');
      this.closeControl = this.dialog.querySelector('.v2-product-quick-view__close');
      this.onClick = this.handleClick.bind(this);
      this.onDialogChange = this.handleDialogChange.bind(this);
      this.onCombinationSelect = this.handleCombinationSelect.bind(this);
      this.onKeyDown = this.handleKeyDown.bind(this);
      this.onKeyUp = this.handleKeyUp.bind(this);
      this.addEventListener('click', this.onClick);
      this.dialog.addEventListener('product-combination-select', this.onCombinationSelect);
      document.addEventListener('keydown', this.onKeyDown, true);
      document.addEventListener('keyup', this.onKeyUp, true);
      this.dialogObserver = new MutationObserver(this.onDialogChange);
      this.dialogObserver.observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.dialog.hasAttribute('open') && event.source === 'product-form') this.dialog.hide();
        });
      }
    }

    disconnectedCallback() {
      clearTimeout(this._dialogRetry);
      this.removeEventListener('click', this.onClick);
      this.dialog?.removeEventListener('product-combination-select', this.onCombinationSelect);
      document.removeEventListener('keydown', this.onKeyDown, true);
      document.removeEventListener('keyup', this.onKeyUp, true);
      this.dialogObserver?.disconnect();
      this.dialogObserver = null;
      this.unsubscribeCart?.();
      this.request?.abort();
      if (this.dialog?.hasAttribute('open')) this.dialog.hide();
      this.dialogBody?.replaceChildren();
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-v2-quick]');
      if (!trigger || trigger.closest('v2-product-quick-view') !== this) return;
      this.openQuickShop(trigger.closest('[data-v2-card], .card__container'), trigger);
    }

    handleDialogChange() {
      if (this.dialog.hasAttribute('open')) return;
      this.request?.abort();
      this.activeTrigger?.setAttribute('aria-expanded', 'false');
      this.activeTrigger = null;
      this.dialogBody.replaceChildren();
    }

    async handleCombinationSelect(event) {
      const link = event.detail?.link;
      if (!link || this.request) return;

      const selectedSizeValue = event.detail?.selectedSizeValue;
      const request = new AbortController();
      this.request = request;
      try {
        const productUrl = this.getCombinationUrl(link);
        const html = await this.fetchProductSection(productUrl, request.signal);
        if (request.signal.aborted) return;
        const handle = new URL(productUrl, location.origin).pathname.split('/').filter(Boolean).pop();
        const product = this.prepareProduct(html, productUrl, handle);
        await this.preloadMainImage(product, request.signal);
        if (request.signal.aborted) return;
        await this.mountProduct(product, selectedSizeValue);
      } catch (error) {
        if (error.name !== 'AbortError' && !request.signal.aborted) {
          console.warn('Product quick shop combination could not load', error);
        }
      } finally {
        if (this.request === request) this.request = null;
      }
    }

    handleKeyDown(event) {
      if (!this.dialog.hasAttribute('open')) return;
      if (event.target === this.closeControl && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        this.closeControl.click();
        return;
      }
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const nested = this.dialog.querySelector('modal-dialog[open]');
      if (nested) nested.hide();
      else this.dialog.hide();
    }

    handleKeyUp(event) {
      if (event.key === 'Escape' && this.dialog.hasAttribute('open')) event.stopImmediatePropagation();
    }

    getCombinationUrl(link) {
      if (link.href) return link.href;
      // Color filter panel options are buttons, so select their color through the option value.
      const url = new URL(link.dataset.combinationProductUrl, location.origin);
      if (link.dataset.combinationOptionValueId) {
        url.searchParams.set('option_values', link.dataset.combinationOptionValueId);
      }
      return url.toString();
    }

    async fetchProductSection(productUrl, signal) {
      const url = new URL(productUrl, location.origin);
      url.searchParams.set('section_id', 'v2-product-card');
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(`Product section returned ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!html.querySelector('.v2-product-card-section .wt-product__wrapper[data-product-handle]')) {
        throw new Error('Product section markup was not returned');
      }
      return html;
    }

    async openQuickShop(card, trigger) {
      if (!card || this.dialog.hasAttribute('open') || this.request) return;
      const request = new AbortController();
      this.request = request;
      this.activeTrigger = trigger;
      const productUrl = card.querySelector('.v2-showcase__image-link, .card__picture-container a.card')?.href || card.dataset.url;
      const originalContent = Array.from(trigger.childNodes);
      const originalAriaLabel = trigger.getAttribute('aria-label');
      const originalMinWidth = trigger.style.minWidth;
      trigger.style.minWidth = `${trigger.getBoundingClientRect().width}px`;
      trigger.replaceChildren();
      trigger.setAttribute('aria-label', this.dataset.loadingLabel);
      trigger.setAttribute('aria-busy', 'true');
      trigger.disabled = true;
      try {
        const html = await this.fetchProductSection(productUrl, request.signal);
        if (request.signal.aborted) return;
        const product = this.prepareProduct(html, productUrl, new URL(productUrl, location.origin).pathname.split('/').filter(Boolean).pop());
        await this.preloadMainImage(product, request.signal);
        if (request.signal.aborted) return;
        await this.mountProduct(product);
        this.dialog.show(trigger);
        trigger.setAttribute('aria-expanded', 'true');
      } catch (error) {
        if (error.name === 'AbortError' || request.signal.aborted) return;
        console.warn('Product quick shop could not load', error);
        const link = document.createElement('a');
        link.href = productUrl;
        link.textContent = this.dataset.detailsLabel;
        this.dialogBody.replaceChildren(link);
        this.dialog.show(trigger);
        trigger.setAttribute('aria-expanded', 'true');
      } finally {
        trigger.replaceChildren(...originalContent);
        trigger.style.minWidth = originalMinWidth;
        if (originalAriaLabel === null) trigger.removeAttribute('aria-label');
        else trigger.setAttribute('aria-label', originalAriaLabel);
        trigger.removeAttribute('aria-busy');
        trigger.disabled = false;
        if (this.request === request) this.request = null;
      }
    }

    async mountProduct(product, selectedSizeValue) {
      const productModals = Array.from(product.querySelectorAll('modal-dialog'));
      this.dialogBody.replaceChildren(product);
      this.dialogBody.append(...productModals);

      const options = product.querySelector('variant-options');
      const form = product.querySelector('form[data-type="add-to-cart-form"]');
      const variantInput = form?.querySelector('input[name="id"]');
      const selectedVariant = options?.querySelector('[data-selected-variant]');
      const initialVariant = selectedVariant ? JSON.parse(selectedVariant.textContent || 'null') : null;
      const detailsLink = product.querySelector('.v2-product-quick-view__details');
      const detailsUrl = detailsLink ? new URL(detailsLink.href, location.origin) : null;
      if (options?.isHighVariantsProduct && initialVariant) options.currentVariant = initialVariant;
      const syncVariantInput = () => {
        const id = options?.currentVariant === null
          ? null
          : options?.currentVariant?.id || options?.dataset.variantId || initialVariant?.id;
        if (detailsLink && detailsUrl) {
          const url = new URL(detailsUrl.href);
          if (id) url.searchParams.set('variant', String(id));
          else url.searchParams.delete('variant');
          detailsLink.href = url.href;
        }
        if (!id || !variantInput) return;
        variantInput.value = String(id);
        variantInput.setAttribute('value', variantInput.value);
      };
      form?.addEventListener('submit', syncVariantInput, { capture: true });
      options?.addEventListener('variantChangeEnd', () => {
        syncVariantInput();
        if (!this.dialog.contains(document.activeElement)) {
          this.dialog.querySelector('.v2-product-quick-view__close')?.focus();
        }
      });
      options?.whenLoaded?.then(() => {
        syncVariantInput();
        if (!selectedSizeValue) return;
        const sizeInput = options.querySelector(
          `input[type="radio"][data-position="2"][value="${CSS.escape(selectedSizeValue)}"]`,
        );
        if (sizeInput && !sizeInput.checked) sizeInput.click();
      });
      syncVariantInput();
    }

    async preloadMainImage(product, signal) {
      const source = product.querySelector('[data-gallery] .wt-product__img');
      if (!source) return;
      source.loading = 'eager';
      await new Promise((resolve) => {
        const image = new Image();
        const timeout = setTimeout(finish, 15000);
        let finished = false;
        function finish() {
          if (finished) return;
          finished = true;
          clearTimeout(timeout);
          signal.removeEventListener('abort', finish);
          image.onload = null;
          image.onerror = null;
          resolve();
        }
        signal.addEventListener('abort', finish, { once: true });
        image.onload = () => {
          if (image.decode) image.decode().catch(() => {}).then(finish);
          else finish();
        };
        image.onerror = finish;
        image.sizes = source.sizes;
        image.srcset = source.srcset;
        image.src = source.src;
      });
    }

    prepareProduct(html, productUrl, handle) {
      const wrapper = html.querySelector('.v2-product-card-section .wt-product__wrapper[data-product-handle]');
      const product = wrapper?.querySelector('.wt-product');
      const gallery = product?.querySelector('gallery-section');
      const info = product?.querySelector('.wt-product__info');
      if (!wrapper || !product || !gallery || !info) throw new Error('Product section markup was not returned');

      wrapper.querySelectorAll('.scroll-trigger.animate--slide-in').forEach((element) => {
        element.classList.remove('scroll-trigger', 'animate--slide-in');
      });
      gallery.removeAttribute('data-product-page');
      gallery.classList.remove('wt-product__gallery--sticky');
      gallery.querySelector('#gallery-loader')?.remove();
      gallery.querySelector('[data-gallery]')?.classList.remove('photoswipe');
      gallery.querySelectorAll('[data-gallery] a[data-pswp-width]').forEach((link) => {
        const media = document.createElement('div');
        media.className = 'v2-product-quick-view__media';
        media.replaceChildren(...link.childNodes);
        link.replaceWith(media);
      });
      gallery.querySelectorAll('.wt-product__img--zoom-cursor').forEach((image) => {
        image.classList.remove('wt-product__img--zoom-cursor');
      });

      const options = product.querySelector('variant-options');
      const originalSection = wrapper.dataset.sectionId;
      const modalSection = `v2-quick-${this.dataset.dialogId}`;
      wrapper.dataset.sectionId = modalSection;
      if (options) {
        options.dataset.originalSection = originalSection;
        options.dataset.section = modalSection;
        options.dataset.updateUrl = 'false';
      }
      const ids = new Map();
      wrapper.querySelectorAll('[id]').forEach((element) => {
        const oldId = element.getAttribute('id');
        if (!oldId) {
          element.removeAttribute('id');
          return;
        }
        let newId = `${this.dataset.dialogId}-${oldId}`;
        if (oldId === `MediaGallery-${originalSection}`) newId = `MediaGallery-${modalSection}`;
        if (oldId === `price-${originalSection}`) newId = `price-${modalSection}`;
        if (oldId === `Sku-${originalSection}`) newId = `Sku-${modalSection}`;
        if (oldId === `product-form-${originalSection}`) newId = `product-form-${modalSection}`;
        if (oldId === `ProductSubmitButton-${originalSection}`) newId = `ProductSubmitButton-${modalSection}`;
        if (oldId.startsWith('ModalClose-')) newId = `ModalClose-${modalSection}-${oldId.slice('ModalClose-'.length)}`;
        ids.set(oldId, newId);
        element.setAttribute('id', newId);
      });
      wrapper.querySelectorAll('[for], [form], [aria-controls], [aria-labelledby], [aria-describedby], [data-modal], a[href^="#"]').forEach((element) => {
        for (const name of ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby']) {
          const value = element.getAttribute(name);
          if (value) element.setAttribute(name, value.split(/\s+/).map((id) => ids.get(id) || id).join(' '));
        }
        for (const name of ['href', 'data-modal']) {
          const value = element.getAttribute(name);
          if (value?.startsWith('#') && ids.has(value.slice(1))) element.setAttribute(name, `#${ids.get(value.slice(1))}`);
        }
      });
      wrapper.querySelectorAll('[data-form-id], [data-zoom-modal-id], [data-input-id], [data-section-id]').forEach((element) => {
        for (const name of ['data-form-id', 'data-zoom-modal-id', 'data-input-id']) {
          const id = element.getAttribute(name);
          if (ids.has(id)) element.setAttribute(name, ids.get(id));
        }
        if (element.dataset.sectionId === originalSection) element.dataset.sectionId = modalSection;
      });
      const viewDetails = document.createElement('a');
      viewDetails.className = 'v2-product-quick-view__details';
      viewDetails.href = productUrl;
      viewDetails.textContent = this.dataset.detailsLabel;
      info.append(viewDetails);
      const title = info.querySelector('.wt-product__name');
      if (title) {
        title.id = `${this.dataset.dialogId}-title`;
        this.dialog.querySelector('[role="dialog"]')?.setAttribute('aria-labelledby', title.id);
      }
      wrapper.classList.add('v2-product-quick-view__product');
      wrapper.dataset.productHandle = handle;
      return wrapper;
    }
  });
}
